import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useModuleSlides, useQuizQuestions, useEducationModules, useUpdateProgress } from '@/hooks/useEducation';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/navigation/Header';
import Footer from '@/components/sections/Footer';
import { SlideViewer } from '@/components/education/SlideViewer';
import { QuizComponent } from '@/components/education/QuizComponent';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, ChevronLeft, ChevronRight, Award } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const ModuleViewer: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const { data: modules } = useEducationModules();
  const { data: slides } = useModuleSlides(moduleId!);
  const { data: quizQuestions } = useQuizQuestions(moduleId!);
  const updateProgress = useUpdateProgress();

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [moduleCompleted, setModuleCompleted] = useState(false);

  const module = modules?.find(m => m.id === moduleId);
  const totalSlides = slides?.length || 0;
  const hasQuiz = (quizQuestions?.length || 0) > 0;

  useEffect(() => {
    if (user && moduleId && slides && slides.length > 0) {
      // Update progress when starting module
      updateProgress.mutate({
        user_id: user.id,
        module_id: moduleId,
        status: 'in_progress',
        progress_percentage: Math.round(((currentSlideIndex + 1) / totalSlides) * (hasQuiz ? 80 : 100))
      });
    }
  }, [currentSlideIndex, user, moduleId, slides, totalSlides, hasQuiz]);

  const handleNextSlide = () => {
    if (currentSlideIndex < totalSlides - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    } else if (hasQuiz && !showQuiz) {
      setShowQuiz(true);
    } else if (!hasQuiz) {
      completeModule();
    }
  };

  const handlePrevSlide = () => {
    if (showQuiz) {
      setShowQuiz(false);
    } else if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const handleQuizComplete = (passed: boolean, score: number) => {
    setQuizCompleted(true);
    
    if (passed) {
      completeModule();
      toast({
        title: "Quiz Passed!",
        description: `You scored ${score}%. Module completed successfully!`,
      });
    } else {
      toast({
        title: "Quiz Failed",
        description: `You scored ${score}%. Please review the material and try again.`,
        variant: "destructive",
      });
    }
  };

  const completeModule = () => {
    if (user && moduleId) {
      setModuleCompleted(true);
      updateProgress.mutate({
        user_id: user.id,
        module_id: moduleId,
        status: 'completed',
        progress_percentage: 100,
        completed_at: new Date().toISOString()
      });
      
      toast({
        title: "Module Completed!",
        description: `You earned ${module?.points || 0} points!`,
      });
    }
  };

  const getProgress = () => {
    if (moduleCompleted) return 100;
    if (showQuiz) return hasQuiz ? 90 : 100;
    return Math.round(((currentSlideIndex + 1) / totalSlides) * (hasQuiz ? 80 : 100));
  };

  const handleContinueToNext = () => {
    const currentModules = modules?.filter(m => m.category_id === module?.category_id) || [];
    const currentIndex = currentModules.findIndex(m => m.id === moduleId);
    
    if (currentIndex < currentModules.length - 1) {
      const nextModule = currentModules[currentIndex + 1];
      navigate(`/education/module/${nextModule.id}`);
    } else {
      navigate(`/education/category/${module?.category_id}`);
    }
  };

  if (!module || !slides) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Module not found</h1>
            <Link to="/education" className="text-primary hover:underline">
              Back to Education
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link 
            to={`/education/category/${module.category_id}`}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Course
          </Link>
          
          <div className="space-y-4">
            <h1 className="text-3xl font-bold">{module.title}</h1>
            <p className="text-muted-foreground">{module.description}</p>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{getProgress()}%</span>
              </div>
              <Progress value={getProgress()} className="h-3" />
            </div>
          </div>
        </div>

        {moduleCompleted ? (
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <Award className="w-6 h-6" />
                Module Completed!
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-green-600 mb-4">
                Congratulations! You've successfully completed this module and earned {module.points} points.
              </p>
              <Button onClick={handleContinueToNext}>
                Continue to Next Module
              </Button>
            </CardContent>
          </Card>
        ) : showQuiz ? (
          <QuizComponent
            moduleId={moduleId!}
            questions={quizQuestions || []}
            onComplete={handleQuizComplete}
          />
        ) : (
          <div className="space-y-6">
            <SlideViewer
              slide={slides[currentSlideIndex]}
              slideNumber={currentSlideIndex + 1}
              totalSlides={totalSlides}
            />
            
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={handlePrevSlide}
                disabled={currentSlideIndex === 0 && !showQuiz}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              <div className="text-sm text-muted-foreground">
                Slide {currentSlideIndex + 1} of {totalSlides}
                {hasQuiz && " + Quiz"}
              </div>
              
              <Button onClick={handleNextSlide}>
                {currentSlideIndex === totalSlides - 1 ? 
                  (hasQuiz ? "Take Quiz" : "Complete Module") : "Next"}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ModuleViewer;